import axios from 'axios';
import DataSource from 'devextreme/data/data_source';
import {prepareRequestOptions} from './async';
import notify from 'devextreme/ui/notify';
import {getFilter} from './dataSourceFilterUtils';
import {vars} from 'utils/variables';
import {saveEditDataToApi} from '../utils/functions';

const {APP_CODES: {SUCCESS}} = vars;

/**
 * @param {string} key
 * @param {string[]} columns
 * @param {string} object
 * @param {object} defFilter
 * @param {object} [fieldsSettings]
 * @param {string|string[]} sorts
 * @param {function} modify
 * @param {function} [paramsModifier]
 * @return {DataSource}
 */
export const getDataSource = ({
																key,
																columns = undefined,
																object,
																defFilter = {},
																fieldsSettings,
																sorts = '-ID',
																modifier = null,
																insertKeys = [],
                                loadAll = false,
                                paramsModifier = null,
															}) => {
	const dataSource = new DataSource(
		{
			key,
			load: async ({isLoadingAll, take, skip, filter}) => {
				let page = (skip + take) / take || -1;
				let apiFilter = fieldsSettings ? getFilter(filter, fieldsSettings) || {} : {};
				
				let requestObj = {
					Filters: {
						...defFilter,
						...apiFilter,
					},
					Sorts: Array.isArray(sorts) ? sorts : [sorts],
					Page: (isLoadingAll || loadAll) ? -1 : page,
					RowsPerPage: take,
					Columns: columns,
				};
				
				if (!Object.keys(requestObj.Filters).length) {
					delete requestObj.Filters;
				}
				
				const response = await axios(prepareRequestOptions({
					operation: `${object}/List`,
					data: requestObj,
				}));
				
				if (response.data.ResponseCode === SUCCESS) {
					const result = response.data.Response[object];
					return modifier ? modifier(result) : result;
				}
				
				notify(response.data.ResponseText, 'error', 2000);
				return [];
			},
      byKey: async (keyValue) => {
        const keyField = key;
        try {
          const cached = dataSource.items().find((item) => String(item[keyField]) === String(keyValue));
          if (cached) return cached;
        } catch (e) {
          // ignore
        }

        // Fallback: try to load single row from API via List + simple equality filter.
        const response = await axios(
          prepareRequestOptions({
            operation: `${object}/List`,
            data: {
              Filters: {
                [keyField]: {'=': keyValue},
              },
              Page: -1,
              RowsPerPage: 1,
              Columns: columns,
              Sorts: Array.isArray(sorts) ? sorts : [sorts],
            },
          }),
        );

        if (response.data.ResponseCode === SUCCESS) {
          const result = response.data.Response[object];
          const items = modifier ? modifier(result) : result;
          const first = Array.isArray(items) ? items[0] : null;
          if (first) return first;
        }

        throw new Error(response?.data?.ResponseText || 'Failed to load row by key');
      },
	      update: async (keyValue, newData) => {
	        if (insertKeys.length > 0) {
	          const oldData = dataSource.items().find((item) => {
	            return String(item[key]) === String(keyValue);
	          });

          if (oldData) {
            const paramsData = {};
            const data = {...oldData, ...newData};

	            insertKeys.forEach((key) => {
	              paramsData[key] = data[key];
	            });

	            paramsData.ID = data.ID;
	            const finalParamsData = paramsModifier ? paramsModifier(paramsData) : paramsData;

	            try {
	              await saveEditDataToApi(`${object}/Mod`, finalParamsData, SUCCESS);
	            } catch (error) {
	              const message = error?.message || String(error);
	              notify(message, 'error', 3000);
	              throw error;
	            }
	          }
	        }
	      },
	      insert: async (data) => {
	        if (insertKeys.length > 0 && Object.keys(data).length > 0) {
	          const paramsData = {};

	          insertKeys.forEach((key) => {
	            if (data.hasOwnProperty(key)) {
	              paramsData[key] = data[key];
	            } else {
	              paramsData[key] = null;
	            }
	          });
	          const finalParamsData = paramsModifier ? paramsModifier(paramsData) : paramsData;

	          try {
	            return await saveEditDataToApi(`${object}/Ins`, finalParamsData, SUCCESS);
	          } catch (error) {
	            const message = error?.message || String(error);
	            notify(message, 'error', 3000);
	            throw error;
	          }
	        }
	      },
      remove: async (key) => {
        const request = await axios(prepareRequestOptions({
          operation: `${object}/Del`,
          data: {
            Params: {
              ID: key,
            },
          },
        }));

        if (request.data.ResponseCode === SUCCESS) {
          return request.data.ResponseCode;
        } else {
          throw new Error(request.data.ResponseText);
        }
      },
		},
	);
	
	return dataSource;
};
